'use client'

import { MicroSite } from '@/api/useGetMicroSiteApi'
import { ShipmentNoteType } from '@/app/book/stores/useCartStore'
import { openChat } from '@/lib/chatProvider'
import { getMicroSiteLogoUrl } from '@/utils/urlHelpers'
import clsx from 'clsx'
import React, { MouseEvent, useEffect, useState } from 'react'
import { Control } from 'react-hook-form'
import { HelpCenterButton } from '../HelpCenter/HelpCenter'
import Icon from '../Icon/Icon'
import Link from '../Link/Link'
import Logo, { Brand } from '../Logo/Logo'
import { ModalButton, ModalButtonRenderProps } from '../Modal/Modal'
import NoteModal from '../NoteModal/NoteModal'
import DropdownLinks from './DropdownLinks'
import Navigation from './Navigation'

export interface ImpersonatingUser {
  id: string
  email: string
  phoneNumber?: string
  name?: string
}

export interface BrandItem {
  brand: Brand
  url: string
}

interface LinksItem {
  name: string
  href: string
}

export type PrimaryHeaderOption =
  | {
      name: string
      href: string
      onClick?: never
      disabled?: boolean
      target?: string
    }
  | {
      name: string
      onClick: (event: MouseEvent<HTMLAnchorElement>) => void
      href?: never
      disabled?: boolean
    }

interface PrimaryHeaderButtonObj {
  name: string
  href?: string
  onClick?: () => void
}

export interface PrimaryHeader {
  type: 'primary'
  accountText?: string
  brands?: BrandItem[]
  links: LinksItem[]
  // button can be the legacy object { name, href } or a React node component
  button?: PrimaryHeaderButtonObj | React.ReactNode
  helpLinks: PrimaryHeaderOption[]
  userLinks: PrimaryHeaderOption[]
}

interface SecondaryHeader {
  accountText?: string
  impersonatingUser?: ImpersonatingUser
  microSite?: MicroSite
  signedIn?: boolean
  hideAccountIcon?: boolean
  type: 'secondary'
  userLinks: PrimaryHeaderOption[]
}

export type SiteHeaderProps = {
  /**
   * Brand name eg. "shipplay" | "shipsticks" etc.
   */
  brand: Brand
  hideAccountIcon?: boolean
  logoLink?: string
  onNoteAdd?: (note: string, type: ShipmentNoteType) => void
} & (PrimaryHeader | SecondaryHeader)

export default function SiteHeader(props: SiteHeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const win = window as unknown as {
      scrollY: number
      requestAnimationFrame: (cb: FrameRequestCallback) => number
      cancelAnimationFrame: (id: number) => void
      addEventListener: (
        type: 'scroll',
        listener: () => void,
        options?: unknown,
      ) => void
      removeEventListener: (
        type: 'scroll',
        listener: () => void,
        options?: unknown,
      ) => void
    }

    if ('IntersectionObserver' in win) {
      const sentinel = document.getElementById('siteheader-scroll-sentinel')
      if (!sentinel) return
      const observer = new IntersectionObserver(
        ([entry]) => {
          const nextValue = !entry.isIntersecting
          setIsScrolled((prev) => (prev === nextValue ? prev : nextValue))
        },
        { root: null, threshold: 0 },
      )

      observer.observe(sentinel)
      return () => {
        observer.disconnect()
      }
    }

    let rafId: number | null = null
    let lastValue = false

    const updateScrollState = (): void => {
      rafId = null
      const nextValue = win.scrollY > 10
      if (nextValue !== lastValue) {
        lastValue = nextValue
        setIsScrolled(nextValue)
      }
    }

    const handleScroll = () => {
      if (rafId !== null) return
      rafId = win.requestAnimationFrame(updateScrollState)
    }

    handleScroll()
    win.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      if (rafId !== null) win.cancelAnimationFrame(rafId)
      win.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const noteModalButtons = [
    {
      text: 'Add to all outbound',
      onCTAClick: (note: string) => {
        props.onNoteAdd?.(note, 'OUTBOUND')
      },
    },
    {
      text: 'Add to all inbound',
      onCTAClick: (note: string) => {
        props.onNoteAdd?.(note, 'INBOUND')
      },
    },
    {
      text: 'Add to all shipments',
      onCTAClick: (note: string) => {
        props.onNoteAdd?.(note, 'OUTBOUND')
        props.onNoteAdd?.(note, 'INBOUND')
      },
    },
  ]

  const handleNoteToOrder = (
    e: MouseEvent<HTMLAnchorElement>,
    modalProps: ModalButtonRenderProps,
  ) => {
    e.preventDefault()
    modalProps.onClick()
  }

  const modalRender = (props: ModalButtonRenderProps) => (
    <Link
      className="type-body-11 visited:text-white lg:h-[unset]"
      onClick={(e) => handleNoteToOrder(e, props)}
    >
      Add note to order
    </Link>
  )

  const isAdmin =
    props.type === 'secondary' ? Boolean(props.impersonatingUser?.email) : null

  return (
    <header
      className="fixed left-0 z-10 w-full"
      style={{ top: 'var(--braze-banner-height, 0px)' }}
    >
      {((props.type === 'primary' && props.brands?.length) ||
        props.type === 'secondary') && (
        <div
          className={clsx(
            'w-full bg-theme-primary text-white overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out',
            props.type === 'primary' && !isScrolled && 'max-h-20 opacity-100',
            props.type === 'primary' &&
              isScrolled &&
              'max-h-0 opacity-0 pointer-events-none',
            props.type === 'primary' && 'hidden lg:block',
          )}
        >
          <div className="container">
            <div
              className={clsx(
                'col-span-full flex justify-between items-center flex-wrap',
                props.type === 'secondary' && 'py-4 lg:py-[unset] lg:h-20',
                isAdmin && 'py-4 lg:py-7',
              )}
            >
              <div
                className={clsx(
                  'flex items-center',
                  props.type === 'primary' &&
                    props.brands?.length &&
                    'h-full ml-auto',
                )}
              >
                <Link
                  href={
                    props.type === 'primary'
                      ? props.brands?.[0]?.url
                      : props.logoLink
                  }
                  className="rounded-none h-full"
                >
                  <Logo
                    src={
                      props.type === 'secondary' && props.microSite
                        ? getMicroSiteLogoUrl(props.microSite)
                        : undefined
                    }
                    brand={
                      props.type === 'primary'
                        ? ((props.brands?.[0].brand + 'textonly') as Brand)
                        : ((props.brand + 'textonly') as Brand)
                    }
                    width={
                      props.type === 'secondary' && props.microSite
                        ? '100'
                        : undefined
                    }
                    height="20"
                  />
                </Link>

                {props.type === 'primary' && props.brands?.length && (
                  <ul
                    className={clsx(
                      'flex items-center gap-x-4 [&_img]:max-h-[16px] pl-4 ml-4 relative h-full [&>li]:h-full [&>li>a]:h-full [&>li>a]:py-4',
                      'before:h-8 before:border-l before:border-neutral-600 before:absolute before:left-0',
                    )}
                  >
                    {props.brands.slice(1).map(({ brand, url }, key) => {
                      const brandName = (brand + 'textonly') as Brand
                      return (
                        <li key={key}>
                          <Link
                            className="rounded-none h-full"
                            href={url}
                            onClick={() => {}}
                          >
                            <Logo brand={brandName} width="auto" height="16" />
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>

              {props.type === 'secondary' && props.impersonatingUser?.email && (
                <div className="flex justify-center col-span-full text-center type-body-11 flex-col mt-6 order-1 w-full lg:w-auto lg:order-none lg:flex-row lg:items-center lg:my-auto">
                  <span>
                    Currently booking for:{' '}
                    <Link
                      href={`/admin/users/${props.impersonatingUser.id}`}
                      className="font-semibold text-white"
                      target="_blank"
                    >
                      {props.impersonatingUser.email}
                    </Link>
                  </span>
                  <div className="flex justify-center lg:ml-6">
                    <ModalButton
                      targetModal="note-modal"
                      render={modalRender}
                    />
                    <NoteModal
                      id="note-modal"
                      title="Add note"
                      rules={{
                        required: {
                          value: true,
                          message: 'Note is required',
                        },
                      }}
                      control={{} as Control}
                      buttons={noteModalButtons}
                    />
                  </div>
                </div>
              )}

              {props.type === 'secondary' && (
                <div className="flex gap-4 md:gap-7 type-body-8">
                  <button
                    type="button"
                    className="relative z-10 flex items-center justify-center min-w-[44px] min-h-[44px] gap-2 hover:underline underline-offset-2 lg:hidden"
                    onClick={openChat}
                    aria-label="Chat Support"
                  >
                    <Icon
                      icon="chat-bubble"
                      className={clsx('w-6 h-6 align-middle')}
                    />
                  </button>
                  <HelpCenterButton
                    render={(buttonProps) => (
                      <button
                        type="button"
                        className="flex items-center gap-2 hover:underline underline-offset-2"
                        {...buttonProps}
                      >
                        <Icon icon="help-circle-outline" className="w-6 h-6" />
                      </button>
                    )}
                  />

                  {/* Account Circle Menu Button */}
                  {!props.hideAccountIcon && (
                    <ModalButton
                      render={(modalProps) => (
                        <DropdownLinks
                          accountText={props.accountText}
                          icon="account-circle"
                          onIconClick={(e) => {
                            // open login modal if not logged in, otherwise default behavior
                            if (!props.signedIn) {
                              e.preventDefault()
                              modalProps.onClick()
                            }
                          }}
                          options={props.userLinks}
                          secondary
                          ariaLabel="Account options menu"
                        />
                      )}
                      targetModal="login-modal"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {props.type === 'primary' && (
        <Navigation
          accountText={props.accountText}
          brand={props.brand}
          brands={props.brands}
          hideAccountIcon={props.hideAccountIcon}
          links={props.links}
          logoLink={props.logoLink}
          button={props.button}
          helpLinks={props.helpLinks}
          userLinks={props.userLinks}
        />
      )}
    </header>
  )
}
